'use client';

/**
 * ObeyaRoomComparisonCard — room-type × vendor cross-tab: one row per
 * room type, one column per vendor, cell is that vendor's items placed
 * in that room (count + per-room total, expandable to the real item
 * list). The total is sum(unit_price), not sum(qty * unit_price) — qty
 * is a building-wide multiplier (how many rooms need it), not a
 * per-room fact, so scaling by it here would answer "what does the
 * whole building cost from this vendor," not "what does this ONE room
 * cost" — see build_room_comparison's docstring.
 *
 * New, additive tab — same lineage as ObeyaCostPlanComparisonCard, keyed
 * on room instead of requirement. Unlike that card's cells (at most one
 * pick per vendor), a room needs many different furniture categories,
 * so a cell here can hold several items from the same vendor — never
 * collapsed into a single product/price the way a requirement's cell
 * is. Self-fetches room_comparison — a dedicated strategy, not the
 * Rooms grid's costplan_details reshaped client-side.
 *
 * Rows render collapsed to their summary (item count + per-room total)
 * by default — that's the actually-comparable unit across six vendor
 * columns; expanding one room's item list is a deliberate zoom-in, not
 * the resting state. One border per room-type row is the only grid
 * line; columns separate by whitespace, not a full lattice.
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — ObeyaRoomComparisonCard
 * @see klyde_edge/klyde_intelligence/projects/queries.py — build_room_comparison
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo, useState } from 'react';
import { FileText } from 'lucide-react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';

// 'budget' | 'mid' | 'premium' | null — null means unclassified (no
// product_type resolved, or the benchmark snapshot is thin/insufficient),
// never a guess. Same three-way tier the ladder's dots use.
type PriceTier = 'budget' | 'mid' | 'premium' | null;

interface RoomComparisonItem {
  line_id: string | null; product_sid: string | null; product_name: string | null;
  product_sku: string | null; image_url: string | null; unit_price: number | null; qty: number;
  // Same reference-only quote fields as Cost Comparison — never
  // substituted into unit_price/per_room_total.
  reaches_quote: boolean; quoted_unit_price: number | null;
  // The VendorResponse this item's price came from — null unless
  // reaches_quote is true. A pointer, not a price.
  quoted_response_sid: string | null;
  tier: PriceTier;
}
interface RoomComparisonSelectedItem {
  line_id: string | null; product_sid: string | null; product_name: string | null;
  product_sku: string | null; image_url: string | null; unit_price: number | null;
  // Unlike a cell's own items, a room's decided items legitimately
  // span several vendors — each one needs to say which.
  vendor_id: string | null; vendor_name: string | null;
  reaches_quote: boolean; quoted_unit_price: number | null;
  quoted_response_sid: string | null;
  tier: PriceTier;
}
interface TierSpread { budget: number; mid: number; premium: number; unclassified: number }
interface RoomComparisonCell {
  item_count: number;
  // Sum of unit_price across the cell's items — cost of furnishing
  // ONE instance of this room from this vendor. Deliberately not
  // qty-scaled (qty is a building-wide multiplier, not a per-room
  // fact) — see build_room_comparison's docstring.
  per_room_total: number;
  items: RoomComparisonItem[];
}
interface RoomComparisonRow {
  key: string; label: string;
  // Keyed by vendor_sid — matches vendor_columns. A vendor with no
  // items placed in this room is simply absent, not a zero-count cell.
  cells: Record<string, RoomComparisonCell>;
  // What's actually been decided for this room so far — real picks,
  // summed once, regardless of which vendor column each one lives
  // under (a room's decided items usually span several vendors, not
  // one). A different question from any single vendor column's total
  // ("what would this room cost from X"), never a duplicate of one.
  // 0/0 is a legitimate, common state — nothing decided yet, not an
  // error. See klyde_edge/docs/mcp_engine_concept.md, "A real gap
  // found building this."
  selected_total: number; selected_count: number;
  selected_items: RoomComparisonSelectedItem[];
  // Candidate tier spread — every real vendor option across every cell
  // in this room, classified against the persisted pricing benchmark.
  // Deliberately NOT a single room verdict: real :SELECTED coverage is
  // too sparse on a live plan to say "this room IS tier X" yet (see
  // docs/mcp_pricing_benchmark.md); this answers the decision-support
  // question a buyer has mid-comparison instead — what's the spread of
  // what I'm choosing between.
  tier_spread: TierSpread;
}
interface VendorColumn { vendor_sid: string; vendor_name: string }
interface RoomComparisonData {
  rows: RoomComparisonRow[]; vendor_columns: VendorColumn[]; row_count: number;
  labels?: Record<string, string>;
}

interface GroupedItem {
  item: RoomComparisonItem; count: number; totalPrice: number | null;
  // Only meaningful when every occurrence in the group reached a real
  // quote — a group mixing quoted and un-quoted placements falls back
  // to the plain list-price total rather than implying a certainty
  // the group as a whole doesn't have.
  allQuoted: boolean; quotedTotal: number | null;
}

// Collapses repeat placements of the same product within one cell into
// one row with a ×N badge. A row here is a room TYPE, not a single
// physical room — the same product legitimately shows up more than
// once when a vendor places it in several room INSTANCES of that type
// (e.g. the same coffee table specified in 2-3 rooms), so this is real
// aggregation, not a dedupe workaround for bad data. Grouped by
// product_sid (falls back to name — some rows have no sid), preserving
// first-seen order.
function groupCellItems(items: RoomComparisonItem[]): GroupedItem[] {
  const order: string[] = [];
  const groups = new Map<string, GroupedItem>();
  items.forEach((item, i) => {
    const key = item.product_sid ?? item.product_name ?? `idx-${i}`;
    const existing = groups.get(key);
    if (existing) {
      existing.count += 1;
      existing.totalPrice = existing.totalPrice != null && item.unit_price != null
        ? existing.totalPrice + item.unit_price
        : existing.totalPrice;
      existing.allQuoted = existing.allQuoted && item.reaches_quote;
      existing.quotedTotal = existing.allQuoted && existing.quotedTotal != null && item.quoted_unit_price != null
        ? existing.quotedTotal + item.quoted_unit_price
        : null;
    } else {
      groups.set(key, {
        item, count: 1, totalPrice: item.unit_price,
        allQuoted: item.reaches_quote, quotedTotal: item.reaches_quote ? item.quoted_unit_price : null,
      });
      order.push(key);
    }
  });
  return order.map((key) => groups.get(key)!);
}

// Same ordered ramp as the pricing-benchmark ladder (light -> dark for
// budget -> mid -> premium) — one visual vocabulary for "which tier",
// taught once, reused everywhere it appears.
const TIER_DOT_COLOR: Record<'budget' | 'mid' | 'premium', string> = {
  budget: 'bg-sky-300', mid: 'bg-sky-500', premium: 'bg-sky-800',
};

function TierDot({ tier }: { tier: PriceTier }) {
  if (!tier) return null;
  return (
    <span
      title={`${tier[0].toUpperCase()}${tier.slice(1)} tier`}
      className={cn('inline-block h-1.5 w-1.5 rounded-full shrink-0', TIER_DOT_COLOR[tier])}
    />
  );
}

function TierSpreadBadge({ spread }: { spread: TierSpread }) {
  const hasAny = spread.budget || spread.mid || spread.premium || spread.unclassified;
  if (!hasAny) return null;
  return (
    <div className="flex items-center gap-1 flex-wrap mt-1">
      {(['budget', 'mid', 'premium'] as const).map((t) => (
        spread[t] > 0 && (
          <span key={t} className="inline-flex items-center gap-1 text-muted-foreground">
            <TierDot tier={t} /> {spread[t]}
          </span>
        )
      ))}
      {spread.unclassified > 0 && (
        <span className={cn(text.muted, 'text-muted-foreground/60')} title="No benchmark tier resolved">
          +{spread.unclassified} unclassified
        </span>
      )}
    </div>
  );
}

export function ObeyaRoomComparisonCard() {
  const { mcpId } = useViewportSelection();
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const { contentKey } = useCardIdentity();
  // Rows start collapsed (summary only); expanding is row-by-row, not
  // cell-by-cell. Row heights otherwise vary ~10x between a 14-item
  // room and a 2-item one, which defeats comparing across rows — the
  // summary line is what's comparable, so that's the resting state.
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const strategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'room_comparison'));
  const { data, isLoading } = useStrategyData<RoomComparisonData>(
    strategy, { semantic_id: mcpId ?? '' }, !!mcpId && !!strategy,
  );

  const rows = data?.data?.rows ?? [];
  const vendorColumns = data?.data?.vendor_columns ?? [];
  const L = data?.data?.labels ?? {};

  const toggleRow = (rowKey: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey); else next.add(rowKey);
      return next;
    });
  };

  // Narrow param type — only product_sid/product_name are actually used,
  // so both a cell's own items and a room's selected_items (a different
  // shape, no qty) can share this one handler.
  const selectItem = (item: Pick<RoomComparisonItem, 'product_sid' | 'product_name'>, vendorName: string) => {
    if (!item.product_sid) return;
    selectNode(
      { id: item.product_sid, label: item.product_name ?? 'Product', type: 'product', properties: {} },
      undefined,
      { id: '', label: vendorName, type: 'vendor', properties: {} },
    );
  };

  // Separate target from selectItem — a quoted price and its product
  // are two different things to open, not one click doing double duty.
  const selectVendorResponse = (responseSid: string, vendorName: string) => {
    selectNode({ id: responseSid, label: vendorName, type: 'vendor_response', properties: {} });
  };

  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_room_comparison',
    status: (data ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: null,
    text: `Room comparison: ${rows.length} room types × ${vendorColumns.length} vendors`,
    json: null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, data, mcpId, strategy, rows.length, vendorColumns.length]));

  if (!mcpId) return null;

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto">
        {/* No table-wide w-full: each vendor column gets a floor width
            (min-w) so it never compresses below readable, and the
            table grows past the viewport instead — the scroll lives
            on this wrapper's overflow-auto. At 6 vendors that's a
            no-op; at 20 it's the difference between a horizontal
            scroll and unreadable columns again. */}
        <table className="text-xs border-collapse">
          <thead className="sticky top-0 z-10">
            <tr>
              <th className="sticky left-0 z-20 bg-card border-b border-r border-border px-3 py-2 text-left w-[160px] min-w-[160px] max-w-[160px]">
                Room
              </th>
              {/* Pinned right after Room, same reasoning as Cost
                  Comparison's Selected column — stays visible
                  regardless of how far the vendor columns scroll.
                  Width locked on both th and td (not just th) —
                  border-collapse auto layout otherwise lets body
                  content push a column wider than its declared width,
                  which misaligns every sticky column after it. */}
              <th className="sticky left-[160px] z-20 bg-card border-b border-r border-border px-3 py-2 text-left w-[160px] min-w-[160px] max-w-[160px]">
                {L.selected_column ?? 'Selected'}
              </th>
              {vendorColumns.map((vc) => (
                <th key={vc.vendor_sid} className="bg-card border-b border-border px-3 py-2 text-left font-medium min-w-[220px]">
                  {vc.vendor_name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && rows.length === 0 ? (
              <tr><td colSpan={vendorColumns.length + 2} className="px-3 py-6 text-center text-muted-foreground">…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={vendorColumns.length + 2} className="px-3 py-6 text-center text-muted-foreground">Nothing placed into a room yet</td></tr>
            ) : rows.map((row) => {
              const isOpen = expandedRows.has(row.key);
              return (
              <tr key={row.key} className="border-b border-border">
                <td className="sticky left-0 z-10 bg-card border-r border-border px-3 py-2.5 align-top w-[160px] min-w-[160px] max-w-[160px]">
                  <button
                    type="button"
                    onClick={() => toggleRow(row.key)}
                    className="flex items-center gap-1.5 text-left hover:text-primary"
                    title={isOpen ? 'Collapse room' : 'Expand room'}
                  >
                    <span className={cn('text-muted-foreground transition-transform', isOpen && 'rotate-90')}>▸</span>
                    <span className={cn(text.body, 'font-medium')}>{row.label || '—'}</span>
                  </button>
                  <TierSpreadBadge spread={row.tier_spread} />
                </td>
                <td className="sticky left-[160px] z-10 bg-card border-r border-border px-3 py-2.5 align-top w-[160px] min-w-[160px] max-w-[160px]">
                  {row.selected_count > 0 ? (
                    <>
                      <button
                        type="button"
                        onClick={() => toggleRow(row.key)}
                        className="text-left hover:text-primary"
                      >
                        <span className={cn(text.value, 'block')}>
                          ${row.selected_total.toLocaleString()}
                        </span>
                        <span className={cn(text.muted)}>
                          {row.selected_count} item{row.selected_count !== 1 ? 's' : ''} decided
                        </span>
                      </button>
                      {/* Same expand-on-row-open pattern as a vendor
                          cell's item list — the resting summary is
                          what's comparable at a glance; the actual
                          products are a deliberate zoom-in, not shown
                          twice at different densities by default. */}
                      {isOpen && (
                        <ul className="mt-2 space-y-1.5">
                          {row.selected_items.map((item, i) => (
                            <li key={`${item.line_id ?? item.product_sid ?? 'noid'}-${i}`} className="flex items-center gap-2">
                              {item.image_url ? (
                                // eslint-disable-next-line @next/next/no-img-element
                                <img src={item.image_url} alt="" className="h-10 w-10 rounded object-cover flex-shrink-0" />
                              ) : (
                                <div className="h-10 w-10 rounded bg-muted flex-shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  <TierDot tier={item.tier} />
                                  <button
                                    type="button"
                                    onClick={() => selectItem(item, item.vendor_name ?? '')}
                                    className={cn(text.muted, 'text-left leading-snug truncate hover:text-primary hover:underline')}
                                  >
                                    {item.product_name ?? '—'}
                                  </button>
                                </div>
                                {/* Which vendor this decided item came
                                    from — a room's decided items
                                    legitimately span several, unlike a
                                    vendor cell's own list where it's
                                    implied by the column. */}
                                <div className={cn(text.muted, 'truncate')}>{item.vendor_name ?? '—'}</div>
                                {item.reaches_quote && item.quoted_unit_price != null ? (
                                  <div className="flex items-center gap-1">
                                    <span className="tabular-nums font-semibold text-foreground">
                                      ${item.quoted_unit_price.toLocaleString()}
                                    </span>
                                    {item.quoted_response_sid && (
                                      <button
                                        type="button"
                                        onClick={() => selectVendorResponse(item.quoted_response_sid!, item.vendor_name ?? '')}
                                        className="text-muted-foreground hover:text-primary"
                                        title={`View ${item.vendor_name ?? 'vendor'}'s response`}
                                      >
                                        <FileText className="h-3 w-3" />
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  <span className="tabular-nums text-foreground">
                                    {item.unit_price != null ? `$${item.unit_price.toLocaleString()}` : '—'}
                                  </span>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <span className="text-muted-foreground/60">{L.not_yet_decided ?? 'Not yet decided'}</span>
                  )}
                </td>
                {vendorColumns.map((vc) => {
                  const cell = row.cells[vc.vendor_sid];
                  if (!cell) {
                    return (
                      <td key={vc.vendor_sid} className="px-3 py-2.5 align-top text-muted-foreground/40">
                        —
                      </td>
                    );
                  }
                  return (
                    <td key={vc.vendor_sid} className="px-3 py-2.5 align-top">
                      <button
                        type="button"
                        onClick={() => toggleRow(row.key)}
                        className="text-left hover:text-primary"
                      >
                        {/* Total is the comparable signal across a row —
                            it gets the size and weight. Item count is
                            context, not comparison, so it stays small
                            and muted, trailing the total. */}
                        <span className={cn(text.value, 'block')}>
                          ${cell.per_room_total.toLocaleString()}
                        </span>
                        <span className={cn(text.muted)}>
                          {cell.item_count} item{cell.item_count !== 1 ? 's' : ''}
                        </span>
                      </button>
                      {isOpen && (
                        <ul className="mt-2 space-y-1.5">
                          {groupCellItems(cell.items).map((g, i) => (
                            <li key={`${g.item.line_id ?? g.item.product_sid ?? 'noid'}-${i}`} className="flex items-center gap-2">
                              {g.item.image_url ? (
                                // eslint-disable-next-line @next/next/no-img-element
                                <img src={g.item.image_url} alt="" className="h-10 w-10 rounded object-cover flex-shrink-0" />
                              ) : (
                                <div className="h-10 w-10 rounded bg-muted flex-shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  <TierDot tier={g.item.tier} />
                                  <button
                                    type="button"
                                    onClick={() => selectItem(g.item, vc.vendor_name)}
                                    className={cn(text.muted, 'text-left leading-snug hover:text-primary hover:underline')}
                                  >
                                    {g.item.product_name ?? '—'}
                                  </button>
                                  {g.count > 1 && (
                                    <span className="rounded-full bg-muted px-1.5 py-0.5 text-[0.7em] font-medium text-muted-foreground">
                                      ×{g.count}
                                    </span>
                                  )}
                                </div>
                                {g.allQuoted && g.quotedTotal != null ? (
                                  <div>
                                    <div className="flex items-center gap-1">
                                      <span className="tabular-nums font-semibold text-foreground">
                                        ${g.quotedTotal.toLocaleString()}
                                      </span>
                                      {g.item.quoted_response_sid && (
                                        <button
                                          type="button"
                                          onClick={() => selectVendorResponse(g.item.quoted_response_sid!, vc.vendor_name)}
                                          className="text-muted-foreground hover:text-primary"
                                          title={`View ${vc.vendor_name}'s response`}
                                        >
                                          <FileText className="h-3 w-3" />
                                        </button>
                                      )}
                                    </div>
                                    {g.totalPrice != null && (
                                      <div className={cn(text.muted, 'tabular-nums')}>
                                        {L.price_list ?? 'List'} ${g.totalPrice.toLocaleString()}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className={cn(text.muted, 'tabular-nums')}>
                                    {g.totalPrice != null ? `$${g.totalPrice.toLocaleString()}` : '—'}
                                  </span>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  );
                })}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
